'use client';

import { type RefObject, useLayoutEffect } from 'react';

import { getFocusableElements } from '@/app/components/overlays/utils/focusable';

interface UseFocusTrapProps {
  containerRef: RefObject<HTMLElement | null>;
  startGuardRef: RefObject<HTMLDivElement | null>;
  endGuardRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
}

/**
 * Trap focus inside the container using dummy focus guards (start/end).
 * No auto focus logic included.
 */
export const useFocusTrap = ({
  containerRef,
  startGuardRef,
  endGuardRef,
  enabled,
}: UseFocusTrapProps) => {
  useLayoutEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;
    const startEl = startGuardRef.current;
    const endEl = endGuardRef.current;

    const focusables = getFocusableElements(container);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    const handleStartGuardFocus = () => {
      last?.focus({ preventScroll: true });
    };

    const handleEndGuardFocus = () => {
      first?.focus({ preventScroll: true });
    };

    startEl?.addEventListener('focus', handleStartGuardFocus);
    endEl?.addEventListener('focus', handleEndGuardFocus);

    return () => {
      startEl?.removeEventListener('focus', handleStartGuardFocus);
      endEl?.removeEventListener('focus', handleEndGuardFocus);
    };
  }, [containerRef, startGuardRef, endGuardRef, enabled]);
};
